import React, { useState } from "react";
import { ArrowRight, Mail, MapPin, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "../lib/api";
import { trackContactFormSubmission } from "../lib/analytics";

export default function FinalCTA() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        message: form.message.trim() || null,
      });
      setSubmitted(true);
      
      // Track conversion
      trackContactFormSubmission('homepage_contact');
      
      toast.success("Request received. We’ll be in touch within 24 hours.");
      setForm({ name: "", email: "", company: "", message: "" });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Validation error.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="nst-light py-24 lg:py-32">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="relative overflow-hidden rounded-[32px] bg-[#0A1F47] text-white p-10 md:p-16 lg:p-20 border border-[#0A1F47]">
          <div className="absolute inset-0 bg-grid-dark opacity-25" />
          <div className="glow-orb w-[500px] h-[500px] -top-40 -right-20" style={{ background: "#2E6BF6" }} />
          <div className="glow-orb w-[350px] h-[350px] bottom-0 left-0" style={{ background: "#16C47F" }} />

          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="pill-label on-dark">Let’s Build</span>
              <h2 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05]">
                Build Your Next Platform with the <span className="text-[#6B9FFF]">Right Expertise</span>
              </h2>
              <p className="mt-5 text-white/75 text-base md:text-lg leading-relaxed max-w-xl">
                Engage directly with experienced architects and engineers to design and deliver scalable cloud systems.
              </p>

              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#6B9FFF] mt-0.5" />
                  <div>
                    <div className="text-xs uppercase tracking-wider text-white/50 font-semibold">Location</div>
                    <div className="text-white font-medium mt-0.5">Austin, TX</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-[#6B9FFF] mt-0.5" />
                  <div>
                    <div className="text-xs uppercase tracking-wider text-white/50 font-semibold">Email</div>
                    <a href="mailto:kween.baker@nstinstitute.com" className="text-white font-medium mt-0.5 hover:text-[#6B9FFF] break-all">
                      kween.baker@nstinstitute.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              className="bg-white/[0.05] border border-white/10 rounded-2xl p-6 md:p-8 backdrop-blur-sm space-y-4"
            >
              <h3 className="text-xl font-bold">Request an Architecture Review</h3>
              <p className="text-sm text-white/65">Tell us about your platform goals. We’ll respond within 24 hours.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-[#2E6BF6]"
                  required
                />
                <input
                  type="email"
                  placeholder="Work email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-[#2E6BF6]"
                  required
                />
              </div>
              <input
                type="text"
                placeholder="Company"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-[#2E6BF6]"
              />
              <textarea
                rows={4}
                placeholder="Briefly describe your platform needs..."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-[#2E6BF6] resize-none"
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex justify-center items-center gap-2 py-3.5 rounded-xl bg-[#2E6BF6] hover:bg-[#1E55D6] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
                  </>
                ) : submitted ? (
                  <>
                    <CheckCircle2 className="w-5 h-5" /> Request Received
                  </>
                ) : (
                  <>
                    Submit Request <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
